import React from "react";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
} from "../Services/ReactPDFService.jsx";
import { convertToBulletPoints } from "../Utils/Resume/TextFormattingUtils.js";

// Simple ATS Template - Blue name, blue section headers, contact on right, 3-column bullet skills
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingHorizontal: 40,
    paddingTop: 28,
    paddingBottom: 28,
    fontFamily: "Roboto",
  },
  // Header - name/title on left, contact on right
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
    paddingBottom: 0,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    alignItems: "flex-end",
    paddingTop: 6,
  },
  name: {
    fontSize: 24,
    fontWeight: 700,
    color: "#3b82f6",
    letterSpacing: 0.3,
    marginBottom: 2,
  },
  professionalTitle: {
    fontSize: 11,
    color: "#6b7280",
    paddingTop: 10 ,
    marginBottom: 0,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
  },
  contactText: {
    fontSize: 9,
    color: "#555555",
  },
  contactLink: {
    fontSize: 9,
    color: "#3b82f6",
    textDecoration: "underline",
  },
  contactDot: {
    fontSize: 9,
    color: "#999999",
    marginHorizontal: 5,
  },
  // Section header - blue text, no border
  sectionHeaderContainer: {
    marginTop: 10,
    marginBottom: 0,
    paddingBottom: 3,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#3b82f6",
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  // Summary
  summaryText: {
    fontSize: 9.5,
    lineHeight: 1.5,
    color: "#374151",
  },
  // Experience
  experienceItem: {
    marginBottom: 8,
  },
  experienceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 1,
  },
  companyLocation: {
    fontSize: 10,
    color: "#3b82f6",
    marginBottom: 2,
  },
  jobTitle: {
    fontSize: 10,
    color: "#6b7280",
  },
  dateText: {
    fontSize: 9.5,
    color: "#374151",
    textAlign: "right",
  },
  bulletPoint: {
    fontSize: 9.5,
    marginBottom: 2,
    paddingLeft: 12,
    lineHeight: 1.4,
    color: "#374151",
  },
  // Skills - 3 column with bullets
  skillsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  skillItem: {
    width: "33.33%",
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 3,
    paddingRight: 8,
  },
  skillBullet: {
    fontSize: 9.5,
    color: "#374151",
    marginRight: 4,
  },
  skillText: {
    fontSize: 9.5,
    color: "#374151",
    flex: 1,
  },
  // Education
  educationItem: {
    marginBottom: 8,
  },
  educationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  degree: {
    fontSize: 10.5,
    fontWeight: 700,
    color: "#374151",
  },
  school: {
    fontSize: 9.5,
    color: "#6b7280",
    marginTop: 1,
  },
  educationDate: {
    fontSize: 9.5,
    color: "#374151",
    textAlign: "right",
  },
  gpa: {
    fontSize: 9,
    color: "#6b7280",
    marginTop: 2,
  },
  // Projects
  projectItem: {
    marginBottom: 8,
  },
  projectTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    color: "#3b82f6",
  },
  projectTech: {
    fontSize: 9,
    color: "#6b7280",
    fontStyle: "italic",
    marginTop: 1,
  },
  // Certifications
  certItem: {
    flexDirection: "row",
    marginBottom: 3,
  },
  certBullet: {
    fontSize: 9.5,
    color: "#374151",
    marginRight: 6,
  },
  certText: {
    fontSize: 9.5,
    color: "#374151",
    flex: 1,
    lineHeight: 1.4,
  },
  // Languages
  languageItem: {
    flexDirection: "row",
    marginBottom: 3,
  },
  languageName: {
    fontSize: 9.5,
    color: "#374151",
    marginRight: 8,
  },
  languageLevel: {
    fontSize: 9.5,
    color: "#6b7280",
  },
});

// Helper functions
const getSkillName = (skill) => {
  if (typeof skill === "string") return skill;
  if (skill && typeof skill === "object") return skill.name || skill.skill || "";
  return "";
};

const getLanguageName = (lang) => {
  if (typeof lang === "string") return lang;
  if (lang && typeof lang === "object") return lang.name || lang.language || "";
  return "";
};

const getInterestName = (interest) => {
  if (typeof interest === "string") return interest;
  if (interest && typeof interest === "object") return interest.name || interest.title || "";
  return "";
};

// Template-specific bullet renderer using template styles
const renderTemplateBullets = (text) => {
  if (!text) return null;
  let textString = "";
  try {
    textString = typeof text === "string" ? text : String(text || "");
  } catch {
    return null;
  }
  const points = convertToBulletPoints(textString);
  return points.map((point, index) => (
    <View key={index} style={{ flexDirection: "row", marginBottom: 2, paddingLeft: 8 }}>
      <Text style={{ fontSize: 9.5, color: "#374151", marginRight: 5, lineHeight: 1.4 }}>{"\u2022"}</Text>
      <Text style={{ fontSize: 9.5, color: "#374151", lineHeight: 1.4, flex: 1 }}>{point}</Text>
    </View>
  ));
};

const SimpleATSTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
    professionalTitle = "",
  } = resumeData || {};

  // Dynamic styles
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = { ...baseStyles.name, fontSize: fontSize * 1.85 };
      customStyleOverrides.professionalTitle = { ...baseStyles.professionalTitle, fontSize: fontSize * 0.85 };
      customStyleOverrides.sectionTitle = { ...baseStyles.sectionTitle, fontSize: fontSize * 0.92 };
      customStyleOverrides.jobTitle = { ...baseStyles.jobTitle, fontSize: fontSize * 0.72 };
      customStyleOverrides.degree = { ...baseStyles.degree, fontSize: fontSize * 0.8 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.72 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.72 };
      customStyleOverrides.skillText = { ...baseStyles.skillText, fontSize: fontSize * 0.72 };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionHeaderContainer = {
        ...baseStyles.sectionHeaderContainer,
        marginTop: sectionSpacing,
      };
    }

    if (titleColor) {
      customStyleOverrides.name = {
        ...(customStyleOverrides.name || baseStyles.name),
        color: titleColor,
      };
      customStyleOverrides.sectionTitle = {
        ...(customStyleOverrides.sectionTitle || baseStyles.sectionTitle),
        color: titleColor,
      };
      customStyleOverrides.companyLocation = {
        ...baseStyles.companyLocation,
        color: titleColor,
      };
      customStyleOverrides.projectTitle = {
        ...baseStyles.projectTitle,
        color: titleColor,
      };
      customStyleOverrides.contactLink = {
        ...baseStyles.contactLink,
        color: titleColor,
      };
    }

    if (subtitleColor) {
      customStyleOverrides.professionalTitle = {
        ...(customStyleOverrides.professionalTitle || baseStyles.professionalTitle),
        color: subtitleColor,
      };
      customStyleOverrides.jobTitle = {
        ...(customStyleOverrides.jobTitle || baseStyles.jobTitle),
        color: subtitleColor,
      };
    }

    return StyleSheet.create({ ...baseStyles, ...customStyleOverrides });
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Section titles
  const defaultTitles = {
    summary: "Profile",
    workExperience: "Professional Experience",
    education: "Education",
    skills: "Skills",
    projects: "Projects",
    certifications: "Certifications",
    achievements: "Achievements",
    languages: "Languages",
    awards: "Awards",
    volunteering: "Volunteering",
    interests: "Interests",
    industryExpertise: "Industry Expertise",
    publications: "Publications",
    references: "References",
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Combine all skills
  const allSkills = (() => {
    if (Array.isArray(skills)) return skills;
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) return skills.list;
    const combined = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];
    if (combined.length > 0) return combined;
    if (skills.categorized) {
      return [
        ...(skills.categorized.technical || []),
        ...(skills.categorized.soft || []),
        ...(skills.categorized.tools || []),
        ...(skills.categorized.other || []),
      ];
    }
    return [];
  })();

  const uniqueSkills = (() => {
    const seen = new Set();
    return allSkills.filter((skill) => {
      const name = getSkillName(skill).toLowerCase().trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  })();

  // Section header
  const renderSectionHeader = (title) => (
    <View style={mergedStyles.sectionHeaderContainer}>
      <Text style={mergedStyles.sectionTitle}>{title}</Text>
    </View>
  );

  // Section order
  const defaultSectionOrder = [
    "summary", "workExperience", "skills", "projects", "education",
    "certifications", "achievements", "languages", "awards",
    "volunteering", "interests", "industryExpertise", "publications", "references",
  ];

  const customSectionIds = customSections
    .filter((cs) => cs.id && cs.title && cs.items?.length > 0)
    .map((cs) => cs.id);

  const baseSectionOrder = customSectionOrder || defaultSectionOrder;
  const finalSectionOrder = [
    ...baseSectionOrder,
    ...customSectionIds.filter((id) => !baseSectionOrder.includes(id)),
  ];

  // Section components
  const sectionComponents = {
    summary: () =>
      summary && isValidSummary(summary) && (
        <View>
          {renderSectionHeader(getSectionTitle("summary"))}
          <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
        </View>
      ),

    workExperience: () =>
      workExperience.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("workExperience"))}
          {workExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem} wrap={false}>
              <View style={mergedStyles.experienceHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.companyLocation}>
                    {job.company}{job.location ? `, ${job.location}` : ""}
                  </Text>
                  <Text style={mergedStyles.jobTitle}>{job.jobTitle}</Text>
                </View>
                <Text style={mergedStyles.dateText}>
                  {formatDate(job.startDate)} to{" "}
                  {job.isCurrentJob || job.endDate === "Present" || !job.endDate
                    ? "Present"
                    : formatDate(job.endDate)}
                </Text>
              </View>
              {job.description && (
                <View style={{ marginTop: 4 }}>
                  {renderTemplateBullets(parseHtmlToText(job.description))}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    skills: () =>
      uniqueSkills.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("skills"))}
          <View style={mergedStyles.skillsGrid}>
            {uniqueSkills.map((skill, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillBullet}>{"\u2022"}</Text>
                <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
              </View>
            ))}
          </View>
        </View>
      ),

    education: () =>
      education.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("education"))}
          {education.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem} wrap={false}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>{edu.school}</Text>
                  <Text style={mergedStyles.school}>
                    {edu.degree}{edu.fieldOfStudy ? `, ${edu.fieldOfStudy}` : ""}
                    {edu.location ? `, ${edu.location}` : ""}
                  </Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={mergedStyles.educationDate}>
                    {formatDate(edu.startDate)} to {edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                  </Text>
                  {edu.gpa && <Text style={mergedStyles.gpa}>GPA: {edu.gpa}</Text>}
                </View>
              </View>
            </View>
          ))}
        </View>
      ),

    projects: () =>
      projects.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("projects"))}
          {projects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem} wrap={false}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {project.technologies && (
                <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>
              )}
              {project.description && (
                <View style={{ marginTop: 3 }}>
                  {renderTemplateBullets(parseHtmlToText(project.description))}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    certifications: () =>
      certifications.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("certifications"))}
          {certifications.map((cert, index) => (
            <View key={index} style={mergedStyles.certItem} wrap={false}>
              <Text style={mergedStyles.certBullet}>{"\u2022"}</Text>
              <Text style={mergedStyles.certText}>
                {cert.name}
                {(cert.authority || cert.issuer) && ` - ${cert.authority || cert.issuer}`}
                {cert.date && ` (${formatDate(cert.date)})`}
              </Text>
            </View>
          ))}
        </View>
      ),

    achievements: () =>
      achievements.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("achievements"))}
          {achievements.map((achievement, index) => (
            <View key={index} style={{ marginBottom: 3 }}>
              {achievement.description && (
                <View>{renderTemplateBullets(parseHtmlToText(achievement.description))}</View>
              )}
            </View>
          ))}
        </View>
      ),

    languages: () =>
      languages.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("languages"))}
          <View style={mergedStyles.skillsGrid}>
            {languages.map((lang, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillBullet}>{"\u2022"}</Text>
                <Text style={mergedStyles.skillText}>
                  {getLanguageName(lang)}{lang.proficiency ? ` (${lang.proficiency})` : ""}
                </Text>
              </View>
            ))}
          </View>
        </View>
      ),

    awards: () =>
      awards.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("awards"))}
          {awards.map((award, index) => (
            <View key={index} style={mergedStyles.educationItem} wrap={false}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>{award.title || award.name}</Text>
                  {award.issuer && <Text style={mergedStyles.school}>{award.issuer}</Text>}
                </View>
                {award.date && (
                  <Text style={mergedStyles.educationDate}>{formatDate(award.date)}</Text>
                )}
              </View>
              {award.description && (
                <View style={{ marginTop: 3 }}>
                  {renderTemplateBullets(parseHtmlToText(award.description))}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    volunteering: () =>
      volunteering.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("volunteering"))}
          {volunteering.map((vol, index) => (
            <View key={index} style={mergedStyles.experienceItem} wrap={false}>
              <View style={mergedStyles.experienceHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.companyLocation}>{vol.organization}</Text>
                  <Text style={mergedStyles.jobTitle}>{vol.role}</Text>
                </View>
                <Text style={mergedStyles.dateText}>
                  {formatDate(vol.startDate)} to {vol.isCurrentRole ? "Present" : formatDate(vol.endDate)}
                </Text>
              </View>
              {vol.description && (
                <View style={{ marginTop: 4 }}>
                  {renderTemplateBullets(parseHtmlToText(vol.description))}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    interests: () =>
      interests.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("interests"))}
          <View style={mergedStyles.skillsGrid}>
            {interests.map((interest, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillBullet}>{"\u2022"}</Text>
                <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
              </View>
            ))}
          </View>
        </View>
      ),

    industryExpertise: () =>
      industryExpertise.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("industryExpertise"))}
          {industryExpertise.map((exp, index) => (
            <View key={index} style={mergedStyles.certItem}>
              <Text style={mergedStyles.certBullet}>{"\u2022"}</Text>
              <Text style={mergedStyles.certText}>
                {exp.name}{exp.proficiency ? ` - ${exp.proficiency}` : ""}
                {exp.yearsOfExperience ? ` (${exp.yearsOfExperience})` : ""}
              </Text>
            </View>
          ))}
        </View>
      ),

    publications: () =>
      publications.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("publications"))}
          {publications.map((pub, index) => (
            <View key={index} style={mergedStyles.educationItem} wrap={false}>
              <Text style={mergedStyles.degree}>{pub.title}</Text>
              {pub.authors && (
                <Text style={mergedStyles.school}>
                  {Array.isArray(pub.authors) ? pub.authors.join(", ") : pub.authors}
                </Text>
              )}
              <Text style={mergedStyles.educationDate}>
                {pub.journal}{pub.date ? ` \u2022 ${formatDate(pub.date)}` : ""}
              </Text>
            </View>
          ))}
        </View>
      ),

    references: () =>
      references.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("references"))}
          {references.map((ref, index) => (
            <View key={index} style={mergedStyles.educationItem} wrap={false}>
              <Text style={mergedStyles.degree}>{ref.name}</Text>
              <Text style={mergedStyles.school}>
                {ref.title}{ref.company ? ` at ${ref.company}` : ""}
              </Text>
              <Text style={mergedStyles.educationDate}>
                {[ref.email, ref.phone].filter(Boolean).join(" \u2022 ")}
              </Text>
            </View>
          ))}
        </View>
      ),

    // Custom sections
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View wrap={false}>
            {renderSectionHeader(customSection.title)}
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={{ marginBottom: 3 }}>
                {item.content && (
                  <Text style={mergedStyles.summaryText}>{parseHtmlToText(item.content)}</Text>
                )}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {}),
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header - Name/title left, contact right */}
        <View style={mergedStyles.headerContainer}>
          <View style={mergedStyles.headerLeft}>
            <Text style={mergedStyles.name}>
              {personalInfo.firstName} {personalInfo.lastName}
            </Text>
            {(professionalTitle || (workExperience.length > 0 && workExperience[0].jobTitle)) && (
              <Text style={mergedStyles.professionalTitle}>
                {professionalTitle || workExperience[0].jobTitle}
              </Text>
            )}
          </View>
          <View style={mergedStyles.headerRight}>
            {personalInfo.email && (
              <View style={mergedStyles.contactItem}>
                <Text style={mergedStyles.contactLink}>{personalInfo.email}</Text>
                {personalInfo.phone && <Text style={mergedStyles.contactDot}>{"\u2022"}</Text>}
                {personalInfo.phone && (
                  <Text style={mergedStyles.contactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
                )}
              </View>
            )}
            {personalInfo.location && (
              <View style={mergedStyles.contactItem}>
                <Text style={mergedStyles.contactText}>{personalInfo.location}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Render sections in order */}
        {finalSectionOrder.map((sectionId, index) => {
          const SectionComponent = sectionComponents[sectionId];
          const content = SectionComponent ? SectionComponent() : null;
          return content ? <View key={`${sectionId}-${index}`} wrap={true}>{content}</View> : null;
        })}
      </Page>
    </Document>
  );
};

export default SimpleATSTemplate;
